import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNavVisibility } from '../../../layout/useNavVisibility';
import type {
  AdditionalFaq,
  AdditionalFaqList,
} from '../../../types/knowledgeQuestions';
import {
  BlockSkeleton,
  Button,
  Callout,
  EmptyState,
  IconButton,
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconNote,
  IconPlus,
  IconSearch,
  IconSparkle,
  Input,
  QueryGate,
  SectionBody,
  SectionHead,
  Segmented,
  StatusDot,
  useEmbedded,
} from '../../../ui/v2';
import { FaqEditor } from '../../KnowledgeQuestions/FaqEditor';
import {
  useAdditionalFaqs,
  useKnowledgeActions,
  useOpenKnowledgeQuestions,
} from '../../KnowledgeQuestions/knowledgeData';
import {
  canPublishRole,
  type FaqFilter,
  faqListState,
  faqMatches,
  KNOWLEDGE_BASE_HREF,
  KNOWLEDGE_QUESTIONS_BASE,
  sortFaqs,
  syncTone,
} from '../../KnowledgeQuestions/knowledgeModel';

/** The open editor: a new FAQ, or the one with this id. */
type Selection = 'new' | number | null;

const FILTERS: Array<{ key: FaqFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'live', label: 'Live' },
  { key: 'draft', label: 'Draft' },
];

/**
 * Additional FAQs: answers the store wrote for questions Arthur couldn't
 * answer. One compact list, newest first; a row opens its editor in a panel
 * beside the list. Saving new text starts the conflict check; an Owner or
 * Manager publishes from the panel, which checks and asks them to confirm.
 * Published answers reach Arthur together as one file. Opened from the
 * Knowledge Base card; `?add=1` starts a new one.
 */
const AdditionalFaqsSection: React.FC = () => {
  const faqs = useAdditionalFaqs();
  const questions = useOpenKnowledgeQuestions();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<Selection>(() =>
    params.get('add') === '1' ? 'new' : null,
  );
  // New on every open, but not when a new FAQ is first saved, so that save
  // keeps the same editor (and its publish dialog) instead of a fresh one.
  const opens = useRef(0);
  const [editorKey, setEditorKey] = useState(String(selected));
  const open = (next: 'new' | number) => {
    opens.current += 1;
    setSelected(next);
    setEditorKey(`${next}:${opens.current}`);
  };
  // `?add=1` opens the form once; drop it so a reload doesn't reopen it.
  useEffect(() => {
    if (!params.has('add')) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('add');
        return next;
      },
      { replace: true },
    );
  }, [params, setParams]);
  const [filter, setFilter] = useState<FaqFilter>('all');
  const [search, setSearch] = useState('');
  const actions = useKnowledgeActions();
  const nav = useNavVisibility();
  const canPublish = canPublishRole(nav.role, nav.isPlatformAdmin);
  const close = () => setSelected(null);

  const all = useMemo(() => sortFaqs(faqs.data?.results ?? []), [faqs.data]);
  const term = search.trim().toLowerCase();
  const shown = all.filter(
    (faq) =>
      faqMatches(faq, filter) &&
      (!term ||
        faq.question.toLowerCase().includes(term) ||
        faq.answer.toLowerCase().includes(term)),
  );
  const editing =
    typeof selected === 'number'
      ? all.find((faq) => faq.id === selected)
      : undefined;
  const panelOpen = selected === 'new' || editing != null;
  // Embedded in Getting started, the step lists the suggested questions
  // itself and there is no Knowledge base page to go back to.
  const embedded = useEmbedded();
  const suggested = embedded ? 0 : (questions.data?.count ?? 0);

  return (
    <SectionBody measure="overview">
      {!embedded && (
        <Button
          size="sm"
          variant="ghost"
          leading={<IconChevronLeft size={14} />}
          onClick={() => navigate(KNOWLEDGE_BASE_HREF)}
          className="-ml-2.5 mb-2"
        >
          Knowledge base
        </Button>
      )}
      <SectionHead
        title="Additional FAQs"
        description="Answers your agent gives on calls beyond your menu and hours."
        action={
          <>
            {suggested > 0 && (
              <Button
                size="sm"
                leading={<IconSparkle size={14} />}
                onClick={() => navigate(KNOWLEDGE_QUESTIONS_BASE)}
              >
                Review suggested ({suggested})
              </Button>
            )}
            <Button
              size="sm"
              variant="primary"
              leading={<IconPlus size={14} />}
              onClick={() => open('new')}
            >
              Add FAQ
            </Button>
          </>
        }
      />
      <div className="mt-5">
        <QueryGate
          query={faqs}
          errorTitle="Additional FAQs could not be loaded."
          skeleton={<BlockSkeleton rows={4} />}
        >
          {(data: AdditionalFaqList) => (
            <div className="flex items-start gap-5">
              <div
                className={clsx(
                  'min-w-0 flex-1',
                  panelOpen && 'hidden lg:block',
                )}
              >
                <SyncNote data={data} />
                {all.length === 0 ? (
                  <EmptyState
                    compact
                    icon={<IconNote />}
                    title="No additional FAQs yet"
                    description="Answer a question callers asked, or add one yourself."
                  />
                ) : (
                  <>
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                      <Input
                        aria-label="Search FAQs"
                        placeholder="Search FAQs"
                        leading={<IconSearch size={14} />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="min-w-[200px] flex-1"
                      />
                      <Segmented<FaqFilter>
                        aria-label="Show"
                        items={FILTERS.map((f) => ({
                          ...f,
                          count: all.filter((faq) => faqMatches(faq, f.key))
                            .length,
                        }))}
                        value={filter}
                        onChange={setFilter}
                      />
                    </div>
                    {shown.length === 0 ? (
                      <p className="rounded-v2 border border-v2-line px-4 py-6 text-center text-v2-13 text-v2-fg-3">
                        No FAQs match.
                      </p>
                    ) : (
                      <ul
                        aria-label="Additional FAQs"
                        className="divide-y divide-v2-line overflow-hidden rounded-v2 border border-v2-line"
                      >
                        {shown.map((faq) => (
                          <FaqRow
                            key={faq.id}
                            faq={faq}
                            selected={faq.id === selected}
                            onOpen={() => open(faq.id)}
                          />
                        ))}
                      </ul>
                    )}
                    <p className="mt-2 text-v2-12 text-v2-fg-3">
                      {shown.length === all.length
                        ? `${all.length} ${all.length === 1 ? 'FAQ' : 'FAQs'}`
                        : `${shown.length} of ${all.length} FAQs`}
                    </p>
                  </>
                )}
              </div>
              {panelOpen && (
                <aside
                  aria-label={editing ? 'Edit FAQ' : 'New FAQ'}
                  className="v2-slide-in-right v2-scroll w-full shrink-0 rounded-v2-lg border border-v2-line bg-v2-surface p-4 lg:sticky lg:top-4 lg:max-h-[calc(100dvh-13rem)] lg:w-[400px] lg:overflow-y-auto"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="text-v2-14 font-semibold text-v2-fg">
                      {editing ? 'Edit FAQ' : 'New FAQ'}
                    </h3>
                    <IconButton label="Close" onClick={close} className="-mr-2">
                      <IconClose />
                    </IconButton>
                  </div>
                  <FaqEditor
                    key={editorKey}
                    faq={editing ?? null}
                    canPublish={canPublish}
                    publishEnabled={data.publish_enabled}
                    onSave={async (text) => {
                      if (editing) return actions.updateFaq(editing.id, text);
                      const faq = await actions.createFaq(text);
                      setFilter('all');
                      setSearch('');
                      setSelected(faq.id);
                      return faq;
                    }}
                    onCheck={actions.checkFaq}
                    onPublish={actions.publishFaq}
                    onArchive={async (id) => {
                      await actions.archiveFaq(id);
                      close();
                    }}
                    onSupportDraft={actions.supportDraft}
                    onSendToSupport={actions.sendToSupport}
                  />
                </aside>
              )}
            </div>
          )}
        </QueryGate>
      </div>
    </SectionBody>
  );
};

/** One FAQ: the question, two lines of the answer, and whether Arthur uses it. */
const FaqRow: React.FC<{
  faq: AdditionalFaq;
  selected: boolean;
  onOpen: () => void;
}> = ({ faq, selected, onOpen }) => {
  return (
    <li>
      <button
        type="button"
        aria-current={selected || undefined}
        onClick={onOpen}
        className={clsx(
          'flex w-full items-center gap-4 px-4 py-2.5 text-left outline-none transition-colors',
          selected
            ? 'bg-v2-accent/[0.07]'
            : 'hover:bg-v2-sunken/70 focus-visible:bg-v2-sunken',
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-v2-13 font-semibold text-v2-fg">
            {faq.question}
          </span>
          <span className="mt-0.5 line-clamp-2 text-v2-12 text-v2-fg-3">
            {faq.is_live && !faq.has_unpublished_changes
              ? faq.published_answer
              : faq.answer}
          </span>
        </span>
        <FaqState faq={faq} />
        <IconChevronRight size={14} className="shrink-0 text-v2-fg-4" />
      </button>
    </li>
  );
};

/** Live (green) when Arthur uses the answer; otherwise where the draft stands. */
export const FaqState: React.FC<{ faq: AdditionalFaq }> = ({ faq }) => {
  const state = faqListState(faq);
  return (
    <span className="flex shrink-0 flex-col items-end gap-0.5">
      <span
        className={clsx(
          'inline-flex items-center gap-1.5 text-v2-12 font-medium',
          state.tone === 'success'
            ? 'text-v2-success'
            : state.tone === 'neutral'
              ? 'text-v2-fg-3'
              : 'text-v2-fg-2',
        )}
      >
        <StatusDot tone={state.tone} />
        {state.label}
      </span>
      {state.note && (
        <span className="text-v2-11 text-v2-fg-4">{state.note}</span>
      )}
    </span>
  );
};

/** Whether Arthur has the published answers yet. Silent when nothing is live. */
const SyncNote: React.FC<{ data: AdditionalFaqList }> = ({ data }) => {
  if (!data.publish_enabled) {
    return (
      <Callout tone="neutral" className="mb-4">
        You can write and check answers now. Publishing to Arthur isn’t turned
        on for this store yet.
      </Callout>
    );
  }
  const sync = data.sync;
  if (!sync || !data.results.some((f) => f.is_live)) return null;
  const tone = syncTone(sync);
  if (tone === 'success') {
    return (
      <p role="status" className="mb-4 text-v2-12 text-v2-fg-3">
        {sync.message}
      </p>
    );
  }
  return (
    <Callout
      tone={
        tone === 'danger' ? 'danger' : tone === 'warning' ? 'warning' : 'accent'
      }
      className="mb-4"
    >
      <span role="status">{sync.message}</span>
    </Callout>
  );
};

export default AdditionalFaqsSection;
